import type { EffortLevel } from '../hooks/useModels';

interface Props {
  value: EffortLevel | null;
  /** Allowed levels for THIS model (e.g. Sonnet has no 'max') */
  allowed: EffortLevel[];
  onChange: (next: EffortLevel) => void;
  /** Stable across instances — required for radio grouping when multiple rulers render */
  groupName: string;
  disabled?: boolean;
  /** Model display name shown in the ruler header for clarity */
  modelName?: string;
}

const COST_HINT: Record<EffortLevel, string> = {
  low:    '~1×',
  medium: '~2×',
  high:   '~4×',
  xhigh:  '~6×',
  max:    '~8×',
};

const LABEL: Record<EffortLevel, string> = {
  low:    'Low',
  medium: 'Med',
  high:   'High',
  xhigh:  'X-High',
  max:    'Max',
};

export function EffortRuler({ value, allowed, onChange, groupName, disabled, modelName }: Props) {
  const cols = allowed.length;
  return (
    <div class="effort-ruler" role="radiogroup" aria-label="Reasoning effort">
      {modelName && (
        <div class="effort-ruler-header">Thinking depth · {modelName}</div>
      )}
      <div class="effort-ruler-gradient" aria-hidden="true">
        <span class="effort-ruler-gradient-fill" />
      </div>
      <div
        class="effort-ruler-segments"
        style={`grid-template-columns: repeat(${cols}, 1fr);`}
      >
        {allowed.map((lvl) => {
          const checked = value === lvl;
          const isHigh = lvl === 'xhigh' || lvl === 'max';
          return (
            <label
              key={lvl}
              class={`effort-ruler-seg${checked ? ' selected' : ''}${isHigh ? ' high-cost' : ''}`}
            >
              <input
                type="radio"
                name={groupName}
                value={lvl}
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(lvl)}
                aria-label={`${LABEL[lvl]} effort, ${COST_HINT[lvl]} cost`}
              />
              <span class="effort-ruler-seg-label">
                {lvl === 'max' && <span class="effort-ruler-flame" aria-hidden="true">🔥</span>}
                {LABEL[lvl]}
              </span>
              <span class="effort-ruler-seg-cost">{COST_HINT[lvl]}</span>
            </label>
          );
        })}
      </div>
      <div class="effort-ruler-axis" aria-hidden="true">
        <span>Speed</span>
        <span>Intelligence</span>
      </div>
    </div>
  );
}
